import {useIsMobile, useToasts} from "@brg/ui";
import {useTranslation} from "react-i18next";
import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {ProjectsButchController} from "../../../core/controllers/ProjectsButchController";
import { Project, GetProjectsByFilterProps, PageModel} from "@brg/api";
import {useProfile} from "../../../core/hooks/auth/useProfile";
import {SortingTypeIndex} from "../../constants/sortingType";
import {useHistory} from "react-router-dom";
import {useModal} from "../../../core";
import { MagicBoxRow } from "shared/components/MagicBoxRow";
import { RingPositions } from "shared/constants/ringPositionType";
import { FOLDERS_MODE, FolderT } from "shared/pages/designer/components/tabs/const";
import { ModalContainer } from "../sendProjects/styles";
import { SendProjectsEntity } from "../sendProjects/SendProjectsEntity";
import { CompleteDescription, CompleteTitle, CompleteWrapper, NoProjectsButton, NoProjectsWrapper, SendProjectModalWrapper } from "../sendProjects/employStyles";
import { MAX_FOLDERS_PROJECTS_COUNT } from "shared/pages/designer/components/DesignerFolders/DesignerFolders";
import urlApi from "shared/actions/urlApi";

export const NoProjectsCheckbox: FC<{ 
    onClick: () => void, 
    disable: boolean, 
    text: string 
}> = ({ 
    disable, 
    onClick, 
    text  
}) => (
    <MagicBoxRow
        className={'checkbox checkbox-noproject'} 
        checked={disable}
        label={text}
        onClick={onClick}
        isTeamEdit
    />
)

const NoProjects: FC<{title?: string}> = ({ title }) => {
    const {t} = useTranslation();

    return (<CompleteWrapper noProjects>
        <NoProjectsWrapper>
            <CompleteTitle noProjects style={{ color: 'rgba(144, 144, 144, 1)'}}>
                {t('teams.modal.no_projects_title')}
            </CompleteTitle>
        </NoProjectsWrapper>
    </CompleteWrapper>)
}

export const CompleteSend: FC<{title: string, contextName: string, withoutProjects?: boolean, isCompetition?: boolean, id?: string }> = ({title, id, isCompetition, contextName, withoutProjects}) => {

    const modal = useModal();
    const {t} = useTranslation();
    const history = useHistory();

    return (<CompleteWrapper>
        <CompleteTitle fixedWidth>
            {isCompetition ? t('teams.modal.complete_title_competition', { title }) : t('teams.modal.complete_title', {title})}
        </CompleteTitle>
        <CompleteDescription>
            {withoutProjects ? t('teams.modal.complete_description_wp') : t('teams.modal.complete_description', {title})}
        </CompleteDescription>
        <NoProjectsButton onClick={() => {
            modal.hide();
            if (isCompetition) {
                return;
            }
            history.push((!!contextName ? "/teams/" + contextName : "/"))
        }
            
            }>
                {t(`teams.buttons.back_on_page`, { name: title }
            )}
        </NoProjectsButton>
    </CompleteWrapper>)
}



export const FoldersProjectsEdit: FC<{ 
    folder: FolderT, 
    onSubmit?: (folder: FolderT, type: FOLDERS_MODE.edit) => void, 
    callBack?: (t: number) => void
}> = ({ 
    onSubmit,
    callBack,
    folder 
}) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const { error } = useToasts();
    const [isProjectsLoading, setIsProjectsLoading] = useState(true);
    const [time, setT] = useState(new Date().getTime());
    const [projects, setProjects] = useState<Project[]>([]);
    const [filterOpt, setFilterOpt] = useState<GetProjectsByFilterProps>();
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const profile = useProfile();
    const [sorting, setSorting] = useState(1);

    useEffect(
         () => {
            if (profile && profile.accountId && folder) {
                const initFilter = async () => {
                    const data: PageModel<Project> = await urlApi().fetchOthersUserProjects(folder?.entityIds ?? [])
                
                    const filterOthersProjects = data?.data
                        ?.filter((project) => project?.authorId !== profile?.accountId)
                        ?.map((i) => i?.projectId)

                    const filter: any = {
                        authorIds: [profile.accountId],
                        ...(filterOthersProjects?.length ? { projectIds: filterOthersProjects} : {}),
                        projectFilter: sorting,
                        isContextIgnored: true,
                        projectStatus: [1],
                        onlyAuthors: false,
                        contextsNotContains: true,
                    };
    
                    setFilterOpt(filter);
                }

                initFilter()
                .catch(console.error);
            }
        },
        [profile, sorting, folder]
    );
    
    const onSortingChange = useCallback((s: SortingTypeIndex): void => {
        setSorting(s);
        setT(Date.now());
    }, []);

    const alreadyProjects = useMemo(() => {
        return folder?.entityIds
    }, [projects, folder])

    const isEnoughProjects = useMemo(() =>  {
        return projects?.length === 0
    },[projects?.length, alreadyProjects?.length]);

    const noProjectVisible = useMemo(() => (projects?.length === 0), [projects, isProjectsLoading, isEnoughProjects ])

    const onMobileSortingClick = useCallback(() => {
        setSorting(sorting === 1 ? 2 : 1);
    }, [sorting, setSorting]);

    const onSend = useCallback(() => {
        if (selectedProjects?.length === 0) return;
        if (selectedProjects?.length > MAX_FOLDERS_PROJECTS_COUNT) {
            error(t('folders.msg.max_projects', { count: MAX_FOLDERS_PROJECTS_COUNT}))
            return
        }
        onSubmit && onSubmit({ ...folder, entityIds: selectedProjects}, FOLDERS_MODE.edit)
        callBack && callBack(Date.now())

    }, [selectedProjects, callBack, projects])

    useEffect(() => {
        setSelectedProjects(folder?.entityIds || [])
    } , [folder])

    if (!profile) {
        return null;
    }

    return <ModalContainer style={{ overflow: 'auto', height: '100%'}} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
        <SendProjectModalWrapper width={'100%'} directionMobile='column' direction='column' justify={'flex-start'} align={"center"} alignMobile="flex-start" style={{ position: 'relative'}}>
            <SendProjectsEntity 
                noProjectVisible={!noProjectVisible} 
                hideCount={noProjectVisible} 
                hideFilter={noProjectVisible} 
                count={selectedProjects?.length || 0} 
                isDisabledSendButton={(selectedProjects?.length === 0)} 
                onMobileSortingClick={onMobileSortingClick} 
                onSortingChange={onSortingChange} 
                showMobileClose
                sorting={sorting} 
                onSend={onSend} 
                subtitleText={t('folders.modal.edit_subtitle', { folderName: folder?.name })}
                sendText={t('folders.modal.modal_sent_text')}
            >
                {filterOpt &&
                    <ProjectsButchController
                        modal={false}
                        onSelect={projectId => {
                            setSelectedProjects((prev: string[]) => {
                                if (prev.includes(projectId)) return prev.filter(a => a !== projectId);
                                return [...prev, projectId];
                            });
                        }}
                        metaAlwaysVisible
                        isParticipate={alreadyProjects}
                        selectedProjects={selectedProjects}
                        selectable={true}
                        unselectable={true}
                        isSelectRightNeed={isMobile}
                        selectRingPosition={RingPositions.right}
                        filter={filterOpt}
                        disableFilterCheck={true}
                        date={time}
                        onProjectsLoaded={(p) => {setProjects(p)}}
                        isLoading={setIsProjectsLoading}
                        withoutGoToProject={true}
                        withoutQueryParams
                        withOtherProjects
                    />
                }
            </SendProjectsEntity>
            {noProjectVisible && <NoProjects />}
        </SendProjectModalWrapper>
    </ModalContainer>
}import {useIsMobile, useToasts} from "@brg/ui";
import {useTranslation} from "react-i18next";
import {FC, useCallback, useEffect, useMemo, useState} from "react";
import {ProjectsButchController} from "../../../core/controllers/ProjectsButchController";
import { Project, GetProjectsByFilterProps, PageModel} from "@brg/api";
import {useProfile} from "../../../core/hooks/auth/useProfile";
import {SortingTypeIndex} from "../../constants/sortingType";
import {useHistory} from "react-router-dom";
import {useModal} from "../../../core";
import { MagicBoxRow } from "shared/components/MagicBoxRow";
import { RingPositions } from "shared/constants/ringPositionType";
import { FOLDERS_MODE, FolderT } from "shared/pages/designer/components/tabs/const";
import { ModalContainer } from "../sendProjects/styles";
import { SendProjectsEntity } from "../sendProjects/SendProjectsEntity";
import { CompleteDescription, CompleteTitle, CompleteWrapper, NoProjectsButton, NoProjectsWrapper, SendProjectModalWrapper } from "../sendProjects/employStyles";
import { MAX_FOLDERS_PROJECTS_COUNT } from "shared/pages/designer/components/DesignerFolders/DesignerFolders";
import urlApi from "shared/actions/urlApi";

export const NoProjectsCheckbox: FC<{ 
    onClick: () => void, 
    disable: boolean, 
    text: string 
}> = ({ 
    disable, 
    onClick, 
    text  
}) => (
    <MagicBoxRow
        className={'checkbox checkbox-noproject'} 
        checked={disable}
        label={text}
        onClick={onClick}
        isTeamEdit
    />
)

const NoProjects: FC<{title?: string}> = ({ title }) => {
    const {t} = useTranslation();

    return (<CompleteWrapper noProjects>
        <NoProjectsWrapper>
            <CompleteTitle noProjects style={{ color: 'rgba(144, 144, 144, 1)'}}>
                {t('teams.modal.no_projects_title')}
            </CompleteTitle>
        </NoProjectsWrapper>
    </CompleteWrapper>)
}

export const CompleteSend: FC<{title: string, contextName: string, withoutProjects?: boolean, isCompetition?: boolean, id?: string }> = ({title, id, isCompetition, contextName, withoutProjects}) => {

    const modal = useModal();
    const {t} = useTranslation();
    const history = useHistory();

    return (<CompleteWrapper>
        <CompleteTitle fixedWidth>
            {isCompetition ? t('teams.modal.complete_title_competition', { title }) : t('teams.modal.complete_title', {title})}
        </CompleteTitle>
        <CompleteDescription>
            {withoutProjects ? t('teams.modal.complete_description_wp') : t('teams.modal.complete_description', {title})}
        </CompleteDescription>
        <NoProjectsButton onClick={() => {
            modal.hide();
            if (isCompetition) {
                return;
            }
            history.push((!!contextName ? "/teams/" + contextName : "/"))
        }
            
            }>
                {t(`teams.buttons.back_on_page`, { name: title }
            )}
        </NoProjectsButton>
    </CompleteWrapper>)
}



export const FoldersProjectsEdit: FC<{ 
    folder: FolderT, 
    onSubmit?: (folder: FolderT, type: FOLDERS_MODE.edit) => void, 
    callBack?: (t: number) => void
}> = ({ 
    onSubmit,
    callBack,
    folder 
}) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const { error } = useToasts();
    const [isProjectsLoading, setIsProjectsLoading] = useState(true);
    const [time, setT] = useState(new Date().getTime());
    const [projects, setProjects] = useState<Project[]>([]);
    const [filterOpt, setFilterOpt] = useState<GetProjectsByFilterProps>();
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const profile = useProfile();
    const [sorting, setSorting] = useState(1);

    useEffect(
         () => {
            if (profile && profile.accountId && folder) {
                const initFilter = async () => {
                    const data: PageModel<Project> = await urlApi().fetchOthersUserProjects(folder?.entityIds ?? [])
                
                    const filterOthersProjects = data?.data
                        ?.filter((project) => project?.authorId !== profile?.accountId)
                        ?.map((i) => i?.projectId)

                    const filter: any = {
                        authorIds: [profile.accountId],
                        ...(filterOthersProjects?.length ? { projectIds: filterOthersProjects} : {}),
                        projectFilter: sorting,
                        isContextIgnored: true,
                        projectStatus: [1],
                        onlyAuthors: false,
                        contextsNotContains: true,
                    };
    
                    setFilterOpt(filter);
                }

                initFilter()
                .catch(console.error);
            }
        },
        [profile, sorting, folder]
    );
    
    const onSortingChange = useCallback((s: SortingTypeIndex): void => {
        setSorting(s);
        setT(Date.now());
    }, []);

    const alreadyProjects = useMemo(() => {
        return folder?.entityIds
    }, [projects, folder])

    const isEnoughProjects = useMemo(() =>  {
        return projects?.length === 0
    },[projects?.length, alreadyProjects?.length]);

    const noProjectVisible = useMemo(() => (projects?.length === 0), [projects, isProjectsLoading, isEnoughProjects ])

    const onMobileSortingClick = useCallback(() => {
        setSorting(sorting === 1 ? 2 : 1);
    }, [sorting, setSorting]);

    const onSend = useCallback(() => {
        if (selectedProjects?.length === 0) return;
        if (selectedProjects?.length > MAX_FOLDERS_PROJECTS_COUNT) {
            error(t('folders.msg.max_projects', { count: MAX_FOLDERS_PROJECTS_COUNT}))
            return
        }
        onSubmit && onSubmit({ ...folder, entityIds: selectedProjects}, FOLDERS_MODE.edit)
        callBack && callBack(Date.now())

    }, [selectedProjects, callBack, projects])

    useEffect(() => {
        setSelectedProjects(folder?.entityIds || [])
    } , [folder])

    if (!profile) {
        return null;
    }

    return <ModalContainer style={{ overflow: 'auto', height: '100%'}} justify="flex-start" offsetTop={88} offsetTopMobile={1}>
        <SendProjectModalWrapper width={'100%'} directionMobile='column' direction='column' justify={'flex-start'} align={"center"} alignMobile="flex-start" style={{ position: 'relative'}}>
            <SendProjectsEntity 
                noProjectVisible={!noProjectVisible} 
                hideCount={noProjectVisible} 
                hideFilter={noProjectVisible} 
                count={selectedProjects?.length || 0} 
                isDisabledSendButton={(selectedProjects?.length === 0)} 
                onMobileSortingClick={onMobileSortingClick} 
                onSortingChange={onSortingChange} 
                showMobileClose
                sorting={sorting} 
                onSend={onSend} 
                subtitleText={t('folders.modal.edit_subtitle', { folderName: folder?.name })}
                sendText={t('folders.modal.modal_sent_text')}
            >
                {filterOpt &&
                    <ProjectsButchController
                        modal={false}
                        onSelect={projectId => {
                            setSelectedProjects((prev: string[]) => {
                                if (prev.includes(projectId)) return prev.filter(a => a !== projectId);
                                return [...prev, projectId];
                            });
                        }}
                        metaAlwaysVisible
                        isParticipate={alreadyProjects}
                        selectedProjects={selectedProjects}
                        selectable={true}
                        unselectable={true}
                        isSelectRightNeed={isMobile}
                        selectRingPosition={RingPositions.right}
                        filter={filterOpt}
                        disableFilterCheck={true}
                        date={time}
                        onProjectsLoaded={(p) => {setProjects(p)}}
                        isLoading={setIsProjectsLoading}
                        withoutGoToProject={true}
                        withoutQueryParams
                        withOtherProjects
                    />
                }
            </SendProjectsEntity>
            {noProjectVisible && <NoProjects />}
        </SendProjectModalWrapper>
    </ModalContainer>
}